import { AnalyticsEvent, AnalyticsService } from '../../core/services/analytics-service';
import { CustomerBiller } from '../../billers/models/model';
import { SubAccountWithBillerData } from '../../billers/models/sub-account.model';
import { DelegationData } from '../models/delegation-data.model';
import { DialogService } from '../../core/services/dialog.service';
import { Injectable } from '@angular/core';
import {
  BillerSubAccountDelegateConfirmationDialogComponent,
} from 'src/app/shared-dialogs/biller-sub-account-delegate-confirmation-dialog/biller-sub-account-delegate-confirmation-dialog.component';
import {
  LiabilityDelegateConfirmationDialogComponent,
} from 'src/app/shared-dialogs/liability-delegate-confirmation-dialog/liability-delegate-confirmation-dialog.component';
import { MAX_WIDTH_90, WIDTH_500 } from '../configs/dialog-config';
import { LiabilitiesDelegateForm } from '../forms/liabilities-delegate-form';
import { filter, switchMap, tap } from 'rxjs/operators';
import { LiabilitiesDelegateApiService } from './liabilities-delegate-api.service';
import { Observable } from 'rxjs';
import {
  DeleteLiabilityDelegationConfirmationDialogComponent,
} from 'src/app/shared-dialogs/delete-liability-delegation-confirmation-dialog/delete-liability-delegation-confirmation-dialog.component';
import { Liability } from 'src/app/liabilities/models/liability.model';

import {
  DeleteBillerSubAccountDelegationConfirmationDialogComponent,
} from '../../shared-dialogs/delete-biller-sub-account-delegation-confirmation-dialog/delete-biller-sub-account-delegation-confirmation-dialog.component';
import format from '../utils/biller-subaccount-input-formatter';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root',
})
export class LiabilitiesDelegateService {

  constructor(
    private dialogService: DialogService,
    private snackbar: MatSnackBar,
    private liabilitiesDelegateApiService: LiabilitiesDelegateApiService,
    private analyticsService: AnalyticsService,
  ) {
  }

  public showDialogForLiabilityType(form: LiabilitiesDelegateForm, description: string): Observable<void> {
    return this.dialogService
      .open(LiabilityDelegateConfirmationDialogComponent, {
        data: { description, targetEmail: form.emailAddress.value },
        maxWidth: MAX_WIDTH_90,
        width: WIDTH_500,
      })
      .afterClosed()
      .pipe(
        filter((confirmed) => confirmed),
        switchMap(() => this.liabilitiesDelegateApiService.delegateSingleLiability(form.getSingleLiabilityFinalData())),
      );
  }

  public showDialogForBillerType(form: LiabilitiesDelegateForm, description: string): Observable<void> {
    return this.openBillerSubAccountDialog(description, form.emailAddress.value).pipe(
      filter((confirmed) => confirmed),
      switchMap(() => this.liabilitiesDelegateApiService.delegateForCustomerBiller(form.getCustomerBillerFinalData())),
    );
  }

  public showDialogForSubAccountType(form: LiabilitiesDelegateForm, description: string): Observable<void> {
    return this.openBillerSubAccountDialog(description, form.emailAddress.value).pipe(
      filter((confirmed) => confirmed),
      switchMap(() => this.liabilitiesDelegateApiService.delegateForSubAccount(form.getSubAccountFinalData())),
    );
  }

  public showDeleteDialogForLiabilityType(delegationData: DelegationData, liabilityId: string): Observable<Liability> {
    return this.dialogService
      .open(DeleteLiabilityDelegationConfirmationDialogComponent, {
        data: { targetEmail: delegationData.targetEmail },
        width: WIDTH_500,
        maxWidth: MAX_WIDTH_90,
      })
      .afterClosed()
      .pipe(
        filter((confirmed) => confirmed),
        switchMap(() => this.liabilitiesDelegateApiService.deleteSingleLiabilityDelagation(liabilityId)),
        tap(() => {
          // this.snackbar.open(`Przekazywanie opłaty zostało wyłączone`, null, { duration: DURATION_5S }),
          this.analyticsService.trackEvent(new AnalyticsEvent('Delegation deleted for liability', null));
        }),
      );
  }

  public showDeleteDialogForCustomerBillerType(customerBiller: CustomerBiller): Observable<any> {
    const description = this.findBillerDelegatedSubAccounts(customerBiller);

    return this.dialogService
      .open(DeleteBillerSubAccountDelegationConfirmationDialogComponent, {
        data: { description, targetEmail: customerBiller.delegationData.targetEmail },
        width: WIDTH_500,
        maxWidth: MAX_WIDTH_90,
      })
      .afterClosed()
      .pipe(
        filter((confirmed) => confirmed),
        switchMap(() => this.liabilitiesDelegateApiService.deleteCustomerBillerDelagation(customerBiller.id)),
        tap(() => {
          // this.snackbar.open(`Przekazywanie opłat zostało wyłączone`, null, { duration: DURATION_5S }),
          this.analyticsService.trackEvent(new AnalyticsEvent('Delegation deleted for customer biller', null));
        }),
      );
  }

  public showDeleteDialogForSubAccountType(subAccount: SubAccountWithBillerData): Observable<any> {
    return this.dialogService
      .open(DeleteBillerSubAccountDelegationConfirmationDialogComponent, {
        data: { description: subAccount.name, targetEmail: subAccount.delegationData.targetEmail },
        width: WIDTH_500,
        maxWidth: MAX_WIDTH_90,
      })
      .afterClosed()
      .pipe(
        filter((confirmed) => confirmed),
        switchMap(() =>
          this.liabilitiesDelegateApiService.deleteSubAccountDelagation(subAccount.customerBillerId, subAccount.id),
        ),
        tap(() => {
          // this.snackbar.open(`Przekazywanie opłat zostało wyłączone`, null, { duration: DURATION_5S }),
          this.analyticsService.trackEvent(new AnalyticsEvent('Delegation deleted for subaccount', null));
        }),
      );
  }

  private openBillerSubAccountDialog(description: string, targetEmail: string): Observable<any> {
    return this.dialogService
      .open(BillerSubAccountDelegateConfirmationDialogComponent, {
        data: { description, targetEmail },
        maxWidth: MAX_WIDTH_90,
        width: WIDTH_500,
      })
      .afterClosed();
  }

  private findBillerDelegatedSubAccounts(customerBiller: CustomerBiller) {
    const subAccountsNames = customerBiller.subAccounts
      .filter((sa) => !!sa.delegationData.targetEmail)
      .map((sa) => sa.name);

    const billerName = customerBiller.biller.name;

    return format(subAccountsNames, billerName);
  }
}
